/**
 * syncUtils.js
 * 
 * Utilities for automatic synchronization of survey structure across languages.
 * 
 * CONCEPT:
 * - Survey has one "main language" (Polish 'pl') that defines the structure
 * - Other languages are translations that must match the structure exactly
 * - Structure includes: questionType, pageNumber, isMetric, options.length, textFields, skipLogic
 * - Only content is language-specific: questionContent, options[].text, attachmentLabel
 * 
 * USAGE IN COMPONENT:
 * 
 * 1. Import utilities:
 *    import { autoSyncSurveyStructure, initializeLanguageStructure, ... } from './syncUtils';
 * 
 * 2. Manage active languages state:
 *    const [activeLanguages, setActiveLanguages] = useState(['pl']);
 * 
 * 3. Wrap setSurvey to auto-sync on every change:
 *    const setSurvey = useCallback((updater) => {
 *      setSurveyRaw((prev) => {
 *        const updated = typeof updater === 'function' ? updater(prev) : updater;
 *        return autoSyncSurveyStructure(updated, activeLanguages, 'pl');
 *      });
 *    }, [activeLanguages]);
 * 
 * 4. Enable/disable languages:
 *    - To enable: setSurvey(prev => initializeLanguageStructure(prev, 'en', 'pl'))
 *    - To disable: setSurvey(prev => removeLanguageFromSurvey(prev, 'en', 'pl'))
 * 
 * GUARANTEES:
 * - All active languages will have identical structure
 * - Translations are preserved when structure changes
 * - New languages get empty translations with correct structure
 */

import { syncStructureOnLanguageSwitch } from "./utils/languageUtils";

/**
 * Automatycznie synchronizuje strukturę ankiety ze wszystkich aktywnych języków
 * bazując na języku źródłowym (domyślnie 'pl')
 * 
 * @param {Object} survey - Obiekt ankiety
 * @param {Array<string>} activeLanguages - Lista aktywnych języków do synchronizacji
 * @param {string} sourceLanguage - Język źródłowy (domyślnie 'pl')
 * @returns {Object} - Ankieta z zsynchronizowaną strukturą
 */
export function autoSyncSurveyStructure(survey, activeLanguages, sourceLanguage = 'pl') {
    if (!survey || !Array.isArray(activeLanguages) || !activeLanguages.length) {
        return survey;
    }

    // Filtruj tylko języki które są aktywne
    const enabledLangs = activeLanguages.filter(lng =>
        survey && Object.prototype.hasOwnProperty.call(survey, lng)
    );

    if (!enabledLangs.length || !enabledLangs.includes(sourceLanguage)) {
        return survey;
    }

    // Użyj istniejącej funkcji syncStructureOnLanguageSwitch
    return syncStructureOnLanguageSwitch(survey, sourceLanguage, activeLanguages);
}

/**
 * Hook-like wrapper do automatycznej synchronizacji po każdej zmianie
 * Używaj tego w setSurvey callbacks
 * 
 * @param {Function} setState - Funkcja setSurvey
 * @param {Array<string>} activeLanguages - Lista aktywnych języków
 * @param {string} sourceLanguage - Język źródłowy
 * @returns {Function} - Wrapped setState która automatycznie synchronizuje
 */
export function createAutoSyncSetter(setState, activeLanguages, sourceLanguage = 'pl') {
    return (updater) => {
        setState((prev) => {
            const updated = typeof updater === 'function' ? updater(prev) : updater;
            return autoSyncSurveyStructure(updated, activeLanguages, sourceLanguage);
        });
    };
}

/**
 * Inicjalizuje strukturę dla nowego języka bazując na języku źródłowym
 * 
 * @param {Object} survey - Obiekt ankiety
 * @param {string} newLanguage - Nowy język do dodania
 * @param {string} sourceLanguage - Język źródłowy do skopiowania struktury
 * @returns {Object} - Ankieta z nowym językiem
 */
export function initializeLanguageStructure(survey, newLanguage, sourceLanguage = 'pl') {
    if (!survey || !newLanguage) return survey;

    // Jeśli język już istnieje, nie rób nic
    if (Object.prototype.hasOwnProperty.call(survey, newLanguage)) {
        return survey;
    }

    const sourceQs = survey?.[sourceLanguage]?.questionnaire || [];

    // Skopiuj strukturę z języka źródłowego, ale z pustymi tłumaczeniami
    const newQs = sourceQs.map(q => ({
        questionID: q.questionID, // Zachowaj to samo ID!
        pageNumber: q.pageNumber,
        questionType: q.questionType,
        questionContent: '', // Puste tłumaczenie
        isMetric: q.isMetric,
        attachments: q.attachments || [],
        attachmentUrls: q.attachmentUrls || [],
        details: {
            ...q.details,
            // Jeśli są opcje jako obiekty {value, label}, skopiuj wartości ale wyczyść etykiety
            options: Array.isArray(q.details?.options)
                ? q.details.options.map(opt => 
                    typeof opt === 'object' && opt !== null
                        ? { value: opt.value, label: '' } // Zachowaj wartość, wyczyść etykietę
                        : '' // Dla starych stringowych opcji
                  )
                : q.details?.options,
            // selectOptions dla OpenList - również zachowaj wartości, wyczyść etykiety
            selectOptions: Array.isArray(q.details?.selectOptions)
                ? q.details.selectOptions.map(opt =>
                    typeof opt === 'object' && opt !== null
                        ? { value: opt.value, label: '' } // Zachowaj wartość, wyczyść etykietę
                        : { value: '', label: '' }
                  )
                : q.details?.selectOptions,
            // attachmentLabel też może być tłumaczalne
            attachmentLabel: ''
        }
    }));

    return {
        ...survey,
        [newLanguage]: {
            checked: true,
            questionnaire: newQs
        }
    };
}

/**
 * Usuwa język z ankiety
 * 
 * @param {Object} survey - Obiekt ankiety
 * @param {string} language - Język do usunięcia
 * @param {string} sourceLanguage - Język źródłowy (nie można go usunąć)
 * @returns {Object} - Ankieta bez usuniętego języka
 */
export function removeLanguageFromSurvey(survey, language, sourceLanguage = 'pl') {
    if (!survey || !language || language === sourceLanguage) {
        return survey;
    }

    const { [language]: removed, ...rest } = survey;
    return rest;
}

/**
 * Pobiera listę aktywnych języków z ankiety
 * 
 * @param {Object} survey - Obiekt ankiety
 * @param {Array<string>} allLanguages - Wszystkie dostępne języki
 * @returns {Array<string>} - Lista aktywnych języków
 */
export function getActiveLanguages(survey, allLanguages) {
    if (!survey || !Array.isArray(allLanguages)) return [];

    return allLanguages.filter(lng =>
        Object.prototype.hasOwnProperty.call(survey, lng) &&
        Array.isArray(survey?.[lng]?.questionnaire)
    );
}
